import { DateOnly } from "@/shared/components/date-picker";
import { getMomentTz } from "@dexteel/mesf-core";
import {
	GasEntriesData,
	GasEntryDayHeatValue,
	GasEntryDraft,
	GasEntryMeter,
	GasEntryTarget,
	GasEntryTrackedShare,
} from "../models/GasEntry";

const moment = getMomentTz();

const RANKINE_OFFSET = 460;
const ATMOSPHERIC_PRESSURE_PSI = 14.4;
const BASE_PRESSURE_PSIA = 14.73;
const BASE_TEMPERATURE_F = 60;

export interface GasEntryMeterValues {
	uncorrectedVolume: number | null;
	pressurePsi: number | null;
	temperatureF: number | null;
}

export interface GasEntryDayRow {
	date: DateOnly;
	gasEntryId: number | null;
	heatValueBtuPerCf: number | null;
	billHeatValue: GasEntryDayHeatValue | null;
	comments: string | null;
	valuesByMeterId: Record<number, GasEntryMeterValues>;
	percentByTargetId: Record<number, number | null>;
}

export const getMonthDays = (month: DateOnly): DateOnly[] => {
	const start = moment(month, "YYYY-MM-DD").startOf("month");
	const today = moment().startOf("day");
	const days: DateOnly[] = [];
	const day = start.clone();

	while (day.isSame(start, "month") && day.isSameOrBefore(today, "day")) {
		days.push(day.format("YYYY-MM-DD"));
		day.add(1, "day");
	}
	return days;
};

export const buildGasEntryDayRows = ({
	data,
	month,
}: {
	data: GasEntriesData | undefined;
	month: DateOnly;
}): GasEntryDayRow[] => {
	const entries = data?.entries ?? [];

	return getMonthDays(month).map((date) => {
		const entry = entries.find((current) => current.EntryDate === date);
		const billHeatValue =
			data?.dayHeatValues.find((day) => day.EntryDate === date) ?? null;

		if (entry === undefined)
			return {
				date,
				gasEntryId: null,
				heatValueBtuPerCf: null,
				billHeatValue,
				comments: null,
				valuesByMeterId: {},
				percentByTargetId: {},
			};

		const valuesByMeterId: Record<number, GasEntryMeterValues> = {};
		for (const reading of data?.readings ?? []) {
			if (reading.GasEntryId !== entry.GasEntryId) continue;
			valuesByMeterId[reading.GasMeterId] = {
				uncorrectedVolume: reading.UncorrectedVolume,
				pressurePsi: reading.PressurePsi,
				temperatureF: reading.TemperatureF,
			};
		}

		return {
			date,
			gasEntryId: entry.GasEntryId,
			heatValueBtuPerCf: entry.HeatValueBtuPerCf,
			billHeatValue,
			comments: entry.Comments,
			valuesByMeterId,
			percentByTargetId: {},
		};
	});
};

export const getCorrectedMcf = (values: GasEntryMeterValues): number | null => {
	const { uncorrectedVolume, pressurePsi, temperatureF } = values;

	if (
		uncorrectedVolume === null ||
		pressurePsi === null ||
		temperatureF === null
	)
		return null;

	const rankine = RANKINE_OFFSET + temperatureF;
	if (rankine === 0) return null;

	return (
		uncorrectedVolume *
		((pressurePsi + ATMOSPHERIC_PRESSURE_PSI) / BASE_PRESSURE_PSIA) *
		((RANKINE_OFFSET + BASE_TEMPERATURE_F) / rankine)
	);
};

export const getTotalMcf = (
	row: GasEntryDayRow,
	meters: GasEntryMeter[],
): number | null => {
	const corrected = meters
		.map((meter) => row.valuesByMeterId[meter.GasMeterId])
		.filter((values) => values !== undefined)
		.map(getCorrectedMcf)
		.filter((mcf): mcf is number => mcf !== null);

	return corrected.length === 0
		? null
		: corrected.reduce((total, mcf) => total + mcf, 0);
};

export const getEffectiveHeatValue = (row: GasEntryDayRow): number | null =>
	row.heatValueBtuPerCf ?? row.billHeatValue?.HeatValueBtuPerCf ?? null;

export const isHeatValueOverridden = (row: GasEntryDayRow) =>
	row.heatValueBtuPerCf !== null &&
	row.billHeatValue?.GasBillId != null &&
	row.heatValueBtuPerCf !== row.billHeatValue.HeatValueBtuPerCf;

export const isHeatValueStored = (row: GasEntryDayRow) =>
	row.heatValueBtuPerCf !== null &&
	(row.billHeatValue?.GasBillId == null ||
		row.heatValueBtuPerCf !== row.billHeatValue.HeatValueBtuPerCf);

export const getTotalMmbtu = (
	row: GasEntryDayRow,
	meters: GasEntryMeter[],
): number | null => {
	const mcf = getTotalMcf(row, meters);
	const heatValue = getEffectiveHeatValue(row);

	return mcf === null || heatValue === null ? null : (mcf * heatValue) / 1000;
};

export const groupTrackedSharesByDate = (
	trackedShares: GasEntryTrackedShare[],
): Record<DateOnly, GasEntryTrackedShare[]> => {
	const sharesByDate: Record<DateOnly, GasEntryTrackedShare[]> = {};
	for (const share of trackedShares)
		(sharesByDate[share.EntryDate] ??= []).push(share);
	return sharesByDate;
};

export const getTrackedMcf = (
	dayShares: GasEntryTrackedShare[],
): number | null =>
	dayShares.length === 0
		? null
		: dayShares.reduce((total, share) => total + share.TrackedMcf, 0);

export const getTrackedMcfByTargetId = (
	dayShares: GasEntryTrackedShare[],
): Record<number, number> => {
	const mcfByTargetId: Record<number, number> = {};
	for (const share of dayShares)
		if (share.TargetId !== null)
			mcfByTargetId[share.TargetId] =
				(mcfByTargetId[share.TargetId] ?? 0) + share.TrackedMcf;
	return mcfByTargetId;
};

export const getSharePercents = (
	targets: GasEntryTarget[],
	dayShares: GasEntryTrackedShare[],
	totalMcf: number | null,
): Record<number, number | null> => {
	const percentByTargetId: Record<number, number | null> = {};

	if (totalMcf === null || totalMcf <= 0) {
		for (const target of targets) percentByTargetId[target.TargetId] = null;
		return percentByTargetId;
	}

	if (dayShares.length === 0) {
		for (const target of targets)
			percentByTargetId[target.TargetId] = target.DefaultAllocationPercent;
		return percentByTargetId;
	}

	const round = (value: number) => Math.round(value * 10000) / 10000;
	const mcfOf = (targetId: number) =>
		dayShares
			.filter((share) => share.TargetId === targetId)
			.reduce((total, share) => total + share.TrackedMcf, 0);
	const tracked = targets.filter((target) =>
		dayShares.some((share) => share.TargetId === target.TargetId),
	);
	const untracked = targets.filter((target) => !tracked.includes(target));
	const trackedMcf = tracked.reduce(
		(total, target) => total + mcfOf(target.TargetId),
		0,
	);
	const remainderPercent = Math.max(
		0,
		((totalMcf - trackedMcf) / totalMcf) * 100,
	);

	for (const target of targets)
		percentByTargetId[target.TargetId] = tracked.includes(target)
			? round((mcfOf(target.TargetId) / totalMcf) * 100)
			: round(remainderPercent / untracked.length);
	return percentByTargetId;
};

export const getBreakdownMcf = (
	row: GasEntryDayRow,
	targetId: number,
	totalMcf: number | null,
	trackedMcfByTargetId: Record<number, number>,
): number | null => {
	if (totalMcf === null) return null;
	const tracked = trackedMcfByTargetId[targetId];
	if (tracked !== undefined) return tracked;
	const percent = row.percentByTargetId[targetId] ?? null;
	return percent === null ? null : (totalMcf * percent) / 100;
};

const sumOrNull = (values: (number | null)[]) => {
	const read = values.filter((value): value is number => value !== null);
	return read.length === 0
		? null
		: read.reduce((total, value) => total + value, 0);
};

export interface GasEntryMonthTotals {
	uncorrectedVolumeByMeterId: Record<number, number | null>;
	mcfByMeterId: Record<number, number | null>;
	totalMcf: number | null;
	totalMmbtu: number | null;
	heatValueBtuPerCf: number | null;
	breakdownByTargetId: Record<number, number | null>;
	percentByTargetId: Record<number, number | null>;
}

export const getMonthTotals = (
	rows: GasEntryDayRow[],
	meters: GasEntryMeter[],
	targets: GasEntryTarget[],
	sharesByDate: Record<DateOnly, GasEntryTrackedShare[]>,
): GasEntryMonthTotals => {
	const valuesOf = (row: GasEntryDayRow, gasMeterId: number) =>
		row.valuesByMeterId[gasMeterId];

	const uncorrectedVolumeByMeterId: Record<number, number | null> = {};
	const mcfByMeterId: Record<number, number | null> = {};
	for (const meter of meters) {
		uncorrectedVolumeByMeterId[meter.GasMeterId] = sumOrNull(
			rows.map(
				(row) => valuesOf(row, meter.GasMeterId)?.uncorrectedVolume ?? null,
			),
		);
		mcfByMeterId[meter.GasMeterId] = sumOrNull(
			rows.map((row) => {
				const values = valuesOf(row, meter.GasMeterId);
				return values === undefined ? null : getCorrectedMcf(values);
			}),
		);
	}

	const dayTotals = rows.map((row) => ({
		row,
		mcf: getTotalMcf(row, meters),
		mmbtu: getTotalMmbtu(row, meters),
		trackedMcfByTargetId: getTrackedMcfByTargetId(sharesByDate[row.date] ?? []),
	}));

	const totalMcf = sumOrNull(dayTotals.map((day) => day.mcf));
	const totalMmbtu = sumOrNull(dayTotals.map((day) => day.mmbtu));
	const heatedMcf = sumOrNull(
		dayTotals.map((day) => (day.mmbtu === null ? null : day.mcf)),
	);

	const breakdownByTargetId: Record<number, number | null> = {};
	const percentByTargetId: Record<number, number | null> = {};
	for (const target of targets) {
		const breakdown = sumOrNull(
			dayTotals.map((day) =>
				getBreakdownMcf(
					day.row,
					target.TargetId,
					day.mcf,
					day.trackedMcfByTargetId,
				),
			),
		);
		breakdownByTargetId[target.TargetId] = breakdown;
		percentByTargetId[target.TargetId] =
			breakdown === null || totalMcf === null || totalMcf <= 0
				? null
				: (breakdown / totalMcf) * 100;
	}

	return {
		uncorrectedVolumeByMeterId,
		mcfByMeterId,
		totalMcf,
		totalMmbtu,
		heatValueBtuPerCf:
			totalMmbtu === null || heatedMcf === null || heatedMcf <= 0
				? null
				: (totalMmbtu * 1000) / heatedMcf,
		breakdownByTargetId,
		percentByTargetId,
	};
};

export const buildGasEntryDraft = (
	row: GasEntryDayRow,
	meters: GasEntryMeter[],
): GasEntryDraft => ({
	GasEntryId: row.gasEntryId,
	EntryDate: row.date,
	HeatValueBtuPerCf: isHeatValueStored(row) ? row.heatValueBtuPerCf : null,
	Comments: row.comments,
	MeterReadings: [
		...new Set([
			...meters.map((meter) => meter.GasMeterId),
			...Object.keys(row.valuesByMeterId).map(Number),
		]),
	].map((gasMeterId) => {
		const values = row.valuesByMeterId[gasMeterId] ?? {
			uncorrectedVolume: null,
			pressurePsi: null,
			temperatureF: null,
		};
		return {
			GasMeterId: gasMeterId,
			UncorrectedVolume: values.uncorrectedVolume,
			PressurePsi: values.pressurePsi,
			TemperatureF: values.temperatureF,
		};
	}),
});
